/** @format */

import { useState, useCallback } from 'react';
import { AuthService } from '../services/authService';
import { validatePassword } from '../utils/passwordRequirements';

export type PasswordResetStep = 1 | 2 | 3 | 4;

interface PasswordResetFormData {
  username: string;
  confirmationCode: string;
  newPassword: string;
  confirmPassword: string;
}

interface UsePasswordResetReturn {
  step: PasswordResetStep;
  loading: boolean;
  error: string | null;
  formData: PasswordResetFormData;
  setStep: (step: PasswordResetStep) => void;
  updateFormData: (field: keyof PasswordResetFormData, value: string) => void;
  initiateReset: () => Promise<boolean>;
  confirmReset: () => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

const initialFormData: PasswordResetFormData = {
  username: '',
  confirmationCode: '',
  newPassword: '',
  confirmPassword: '',
};

/**
 * Custom hook for password reset flow management
 */
export const usePasswordReset = (): UsePasswordResetReturn => {
  const [step, setStep] = useState<PasswordResetStep>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<PasswordResetFormData>(initialFormData);

  const updateFormData = useCallback((field: keyof PasswordResetFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setStep(1);
    setLoading(false);
    setError(null);
    setFormData(initialFormData);
  }, []);

  const initiateReset = useCallback(async (): Promise<boolean> => {
    if (!formData.username.trim()) {
      setError('Please enter your username');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await AuthService.initiateReset(formData.username);
      setStep(2);
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [formData.username]);

  const confirmReset = useCallback(async (): Promise<boolean> => {
    if (!formData.confirmationCode.trim()) {
      setError('Please enter the verification code');
      return false;
    }

    if (!formData.newPassword) {
      setError('Please enter a new password');
      return false;
    }

    // Validate password requirements
    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError('Password does not meet all requirements');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await AuthService.confirmReset(
        formData.username,
        formData.confirmationCode,
        formData.newPassword
      );
      setStep(4);
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [formData]);

  return {
    step,
    loading,
    error,
    formData,
    setStep,
    updateFormData,
    initiateReset,
    confirmReset,
    clearError,
    reset,
  };
};
